import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Users, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingSkeleton } from '../../components/shared/LoadingSkeleton';
import { ConfirmDialog } from '../../components/shared/ConfirmDialog';
import { Link } from 'react-router-dom';

interface Teacher {
  id: number;
  name: string;
  email: string;
  phone: string;
  status: string;
}

export default function TeachersPage() {
  const queryClient = useQueryClient();
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['teachers'],
    queryFn: async () => api.get<any>('/api/teachers'),
  });

  const teachers: Teacher[] = data?.data || [];

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/teachers/${id}`),
    onSuccess: () => {
      toast.success('Teacher deleted');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['teachers'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete teacher');
    },
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        icon={Users}
        iconColor="text-violet-500"
        title="Teachers"
        subtitle="Manage teaching staff"
        actionTo="/teachers/create"
        actionLabel="Add Teacher"
      />

      {isLoading ? (
        <LoadingSkeleton count={6} />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 sm:hidden">
            {teachers.map((teacher: Teacher, index: number) => (
              <motion.div
                key={teacher.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="glass-card p-3 rounded-2xl"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="w-9 h-9 rounded-lg gradient-violet flex items-center justify-center text-white text-sm font-bold">
                    {teacher.name.charAt(0)}
                  </div>
                  <div className="flex gap-1">
                     <Link to={`/teachers/${teacher.id}/edit`}>
                       <Button variant="ghost" size="icon" className="h-8 w-8">
                         <Edit className="w-4 h-4" />
                       </Button>
                     </Link>
                     <Button
                       variant="ghost"
                       size="icon"
                       className="h-8 w-8 text-light-text-muted hover:text-rose-500"
                       onClick={() => setDeleteConfirm(teacher.id)}
                     >
                       <Trash2 className="w-4 h-4" />
                     </Button>
                   </div>
                 </div>
                 <h3 className="text-sm font-bold text-light-text dark:text-dark-text truncate">{teacher.name}</h3>
                <p className="text-[10px] text-light-text-muted truncate">{teacher.email}</p>
                <p className="text-[10px] text-light-text-muted">{teacher.phone || 'N/A'}</p>
                <div className="mt-2">
                  <span className={`text-[10px] px-2 py-1 rounded-full ${teacher.status === 'active' ? 'bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-300' : 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300'}`}>
                    {teacher.status}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="hidden sm:block glass-card rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-light-border dark:border-dark-border">
                    <th className="text-left text-xs font-semibold text-light-text-muted uppercase px-4 py-3">Name</th>
                    <th className="text-left text-xs font-semibold text-light-text-muted uppercase px-4 py-3">Email</th>
                    <th className="text-left text-xs font-semibold text-light-text-muted uppercase px-4 py-3">Phone</th>
                    <th className="text-left text-xs font-semibold text-light-text-muted uppercase px-4 py-3">Status</th>
                    <th className="text-right text-xs font-semibold text-light-text-muted uppercase px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers.map((teacher: Teacher) => (
                    <tr key={teacher.id} className="border-b border-light-border/50 dark:border-dark-border/50 hover:bg-light-elevated/50 dark:hover:bg-dark-elevated/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg gradient-violet flex items-center justify-center text-white text-sm font-bold">
                            {teacher.name.charAt(0)}
                          </div>
                          <span className="text-sm font-medium text-light-text dark:text-dark-text">{teacher.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-light-text-secondary">{teacher.email}</td>
                      <td className="px-4 py-3 text-sm text-light-text-secondary">{teacher.phone || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${teacher.status === 'active' ? 'bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-300' : 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300'}`}>
                          {teacher.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                           <Link to={`/teachers/${teacher.id}/edit`}>
                             <Button variant="ghost" size="icon" className="h-8 w-8">
                               <Edit className="w-4 h-4" />
                             </Button>
                           </Link>
                           <Button
                             variant="ghost"
                             size="icon"
                             className="h-8 w-8 text-light-text-muted hover:text-rose-500"
                             onClick={() => setDeleteConfirm(teacher.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={deleteConfirm !== null}
        onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}
        title="Delete Teacher"
        onConfirm={() => deleteMutation.mutate(deleteConfirm!)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
